import React, { useCallback, useEffect, useMemo, useState } from "react";
import { APISD } from "../../../../../config/apisd";
import { APIMS } from "../../../../../config/apims";
import "bulma/css/bulma.css";
import "../../../../../index.css";
import swal from "sweetalert";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRectangleList } from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";

const getStorageValue = (...keys) => {
  for (const key of keys) {
    const value = localStorage.getItem(key);
    if (value && value !== "null" && value !== "undefined") return value;
  }
  return "";
};

const getParsedStorageValue = (key) => {
  const value = localStorage.getItem(key);
  if (!value || value === "null" || value === "undefined") return "";

  try {
    return JSON.parse(value) || "";
  } catch (error) {
    return value;
  }
};

const getResponseArray = (response) => {
  const payload = response?.data?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

export default function Leger() {
  document.title = "Leger";

  const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [loadingTypeLeger, setLoadingTypeLeger] = useState(true);
  const [loadingFinalValue, setLoadingFinalValue] = useState(false);
  const [loadingRawValue, setLoadingRawValue] = useState(false);
  const [semesterData, setSemesterData] = useState([]);

  const [typeLegerData, setTypeLegerData] = useState([]);
  const [selectedTypeLeger, setSelectedTypeLeger] = useState("");
  const [finalValueHeaders, setFinalValueHeaders] = useState([]);
  const [finalValueStudents, setFinalValueStudents] = useState([]);
  const [rawValueHeaders, setRawValueHeaders] = useState([]);
  const [rawValueStudents, setRawValueStudents] = useState([]);
  const [rawValueWeights, setRawValueWeights] = useState({});
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState("");

  const [selectedAcademicYear, setSelectedAcademicYear] =
    useState("");

  const [selectedSemesterId, setSelectedSemesterId] =
    useState("");

  const [searchKeyword, setSearchKeyword] = useState("");

  const storageIdentity = useMemo(() => {
    return {
      branchId: localStorage.getItem("ysb_branch_id") || "",
      schoolId: localStorage.getItem("ysb_school_id") || "",
      teacherId: getParsedStorageValue("teacherID"),
      level: getStorageValue("level"),
    };
  }, []);

  const [permission, setPermission] = useState({
    create: 0,
    read: 0,
    update: 0,
    delete: 0,
  });

  /*
  |--------------------------------------------------------------------------
  | Permission
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const level = localStorage.getItem("level");

    const rolePermissions =
      JSON.parse(localStorage.getItem("role_permission")) || [];

    if (level === "developer" || level === "superadmin") {
      setPermission({
        create: 1,
        read: 1,
        update: 1,
        delete: 1,
      });

      return;
    }

    const foundPermission = rolePermissions.find(
      (item) => item?.menu?.url === location.pathname
    );

    if (!foundPermission) {
      navigate("/dashboard");
      return;
    }

    setPermission({
      create: foundPermission?.create || 0,
      read: foundPermission?.read || 0,
      update: foundPermission?.update || 0,
      delete: foundPermission?.delete || 0,
    });
  }, [location.pathname, navigate]);

  /*
  |--------------------------------------------------------------------------
  | Header Axios
  |--------------------------------------------------------------------------
  */

  const fetchParams = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  /*
  |--------------------------------------------------------------------------
  | Mengambil data semester
  |--------------------------------------------------------------------------
  */

  const getSemesterData = useCallback(async () => {
    setLoading(true);

    try {
      const response = await APIMS.get(
        "/api/access/semesters?page=1&limit=1000&ascending=0&search=",
        fetchParams
      );

      if (response?.status === 200) {
        const semesters = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

        setSemesterData(semesters);

        /*
         * Saat halaman pertama kali dibuka atau di-reload:
         * cari semester dengan status aktif.
         */
        const activeSemester =
          semesters.find(
            (semester) => Number(semester?.status) === 1
          ) || semesters[0];

        if (activeSemester) {
          setSelectedAcademicYear(
            activeSemester?.name_year || ""
          );

          setSelectedSemesterId(
            String(activeSemester?.id || "")
          );
        } else {
          setSelectedAcademicYear("");
          setSelectedSemesterId("");
        }
      }
    } catch (error) {
      setSemesterData([]);
      setSelectedAcademicYear("");
      setSelectedSemesterId("");

      swal({
        title: "Gagal",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Gagal mengambil data tahun ajaran dan semester",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [fetchParams]);

  useEffect(() => {
    getSemesterData();
  }, [getSemesterData]);

  /*
  |--------------------------------------------------------------------------
  | Tipe leger untuk tab dinamis
  |--------------------------------------------------------------------------
  */

  const getTypeLeger = useCallback(async () => {
    setLoadingTypeLeger(true);

    try {
      const response = await APISD.get("/api/type-leger", {
        ...fetchParams,
        data: {
          page: 1,
          limit: 1000,
          ascending: true,
          search: "",
          branch: storageIdentity.branchId,
          level: storageIdentity.level,
        },
      });

      /* Balik urutan API agar tipe terbaru berada di tab terakhir. */
      const types = [...getResponseArray(response)].reverse();
      setTypeLegerData(types);
      setSelectedTypeLeger((previous) =>
        types.some((item) => String(item?.id) === String(previous))
          ? previous
          : types[0]?.id || ""
      );
    } catch (error) {
      setTypeLegerData([]);
      setSelectedTypeLeger("");

      swal({
        title: "Gagal",
        text: error?.response?.data?.message || "Gagal mengambil tipe leger",
        icon: "error",
      });
    } finally {
      setLoadingTypeLeger(false);
    }
  }, [fetchParams, storageIdentity.branchId, storageIdentity.level]);

  useEffect(() => {
    getTypeLeger();
  }, [getTypeLeger]);

  /*
  |--------------------------------------------------------------------------
  | Daftar tahun ajaran
  |--------------------------------------------------------------------------
  */

  const academicYears = useMemo(() => {
    const uniqueAcademicYears = [
      ...new Set(
        semesterData
          .map((semester) => semester?.name_year)
          .filter(Boolean)
      ),
    ];

    return uniqueAcademicYears.sort((a, b) =>
      String(b).localeCompare(String(a), "id", {
        numeric: true,
        sensitivity: "base",
      })
    );
  }, [semesterData]);

  /*
  |--------------------------------------------------------------------------
  | Semester berdasarkan tahun ajaran yang dipilih
  |--------------------------------------------------------------------------
  */

  const semestersByAcademicYear = useMemo(() => {
    return semesterData
      .filter(
        (semester) =>
          semester?.name_year === selectedAcademicYear
      )
      .sort((a, b) =>
        String(a?.semester || "").localeCompare(
          String(b?.semester || ""),
          "id",
          {
            numeric: true,
            sensitivity: "base",
          }
        )
      );
  }, [semesterData, selectedAcademicYear]);

  /*
  |--------------------------------------------------------------------------
  | Semester yang sedang dipilih
  |--------------------------------------------------------------------------
  */

  const selectedSemester = useMemo(() => {
    return (
      semesterData.find(
        (semester) =>
          String(semester?.id) ===
          String(selectedSemesterId)
      ) || null
    );
  }, [semesterData, selectedSemesterId]);

  /*
  |--------------------------------------------------------------------------
  | Mengubah tahun ajaran
  |--------------------------------------------------------------------------
  */

  const handleAcademicYearChange = (event) => {
    const academicYear = event.target.value;

    const semesters = semesterData
      .filter(
        (semester) =>
          semester?.name_year === academicYear
      )
      .sort((a, b) =>
        String(a?.semester || "").localeCompare(
          String(b?.semester || ""),
          "id",
          {
            numeric: true,
            sensitivity: "base",
          }
        )
      );

    /*
     * Jika tahun ajaran yang dipilih memiliki semester aktif,
     * pilih semester aktif tersebut.
     *
     * Jika tidak ada semester aktif, pilih semester pertama.
     */
    const activeSemester =
      semesters.find(
        (semester) => Number(semester?.status) === 1
      ) || semesters[0];

    setSelectedAcademicYear(academicYear);

    setSelectedSemesterId(
      activeSemester
        ? String(activeSemester?.id)
        : ""
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Mengubah semester
  |--------------------------------------------------------------------------
  */

  const handleSemesterChange = (semester) => {
    setSelectedSemesterId(String(semester?.id || ""));
  };

  /*
  |--------------------------------------------------------------------------
  | Label semester
  |--------------------------------------------------------------------------
  */

  const getSemesterLabel = (semester) => {
    const semesterName = String(
      semester?.semester || ""
    ).trim();

    if (!semesterName) {
      return "Semester";
    }

    if (
      semesterName
        .toLowerCase()
        .includes("semester")
    ) {
      return semesterName;
    }

    return `Semester ${semesterName}`;
  };

  const academicYearId = useMemo(
    () =>
      selectedSemester?.ysb_academic_year_id ||
      selectedSemester?.academic_year_id ||
      selectedSemester?.ysb_tahun_ajaran_id ||
      "",
    [selectedSemester]
  );

  const getLegerBody = useCallback(
    (subjectId = "") => ({
      ysb_academic_year_id: academicYearId,
      ysb_semester_id: selectedSemesterId,
      ysb_branch_id: storageIdentity.branchId,
      ysb_school_id: storageIdentity.schoolId,
      ysb_teacher_id: storageIdentity.teacherId,
      ...(subjectId ? { ysb_matpel_id: subjectId } : {}),
    }),
    [academicYearId, selectedSemesterId, storageIdentity]
  );

  const hasCompleteIdentity = Boolean(
    academicYearId &&
      selectedSemesterId &&
      storageIdentity.branchId &&
      storageIdentity.schoolId &&
      storageIdentity.teacherId
  );

  const fetchRawValue = useCallback(
    async (subjectId, showError = true) => {
      if (!subjectId || !hasCompleteIdentity) return;

      setLoadingRawValue(true);
      try {
        const response = await APISD.get("/api/leger/nilai-mentah", {
          ...fetchParams,
          params: getLegerBody(subjectId),
        });
        const result = response?.data?.data || {};

        setRawValueHeaders(Array.isArray(result?.header) ? result.header : []);
        setRawValueStudents(
          Array.isArray(result?.data_murid) ? result.data_murid : []
        );
        setRawValueWeights(
          result?.bobot && typeof result.bobot === "object" ? result.bobot : {}
        );
      } catch (error) {
        setRawValueHeaders([]);
        setRawValueStudents([]);
        setRawValueWeights({});

        if (showError) {
          swal({
            title: "Gagal",
            text:
              error?.response?.data?.message ||
              "Gagal mengambil data nilai mentah",
            icon: "error",
          });
        }
      } finally {
        setLoadingRawValue(false);
      }
    },
    [fetchParams, getLegerBody, hasCompleteIdentity]
  );

  const fetchLegerData = useCallback(async () => {
    if (!hasCompleteIdentity) return;

    setLoadingFinalValue(true);
    setSearchKeyword("");

    try {
      const response = await APISD.get("/api/leger/nilai-akhir", {
        ...fetchParams,
        params: getLegerBody(),
      });
      const result = response?.data?.data || {};
      const headers = Array.isArray(result?.header) ? result.header : [];
      const students = Array.isArray(result?.data_murid) ? result.data_murid : [];
      const subjectOptions = headers
        .filter((header) => header?.matpel_id)
        .map((header) => ({
          id: header.matpel_id,
          name: header?.title || "-",
        }));
      const firstSubjectId = subjectOptions[0]?.id || "";

      setFinalValueHeaders(headers);
      setFinalValueStudents(students);
      setSubjects(subjectOptions);
      setSelectedSubjectId(firstSubjectId);

      if (firstSubjectId) {
        await fetchRawValue(firstSubjectId, false);
      } else {
        setRawValueHeaders([]);
        setRawValueStudents([]);
        setRawValueWeights({});
      }
    } catch (error) {
      setFinalValueHeaders([]);
      setFinalValueStudents([]);
      setSubjects([]);
      setSelectedSubjectId("");
      setRawValueHeaders([]);
      setRawValueStudents([]);
      setRawValueWeights({});

      swal({
        title: "Gagal",
        text:
          error?.response?.data?.message ||
          "Gagal mengambil data nilai akhir semester",
        icon: "error",
      });
    } finally {
      setLoadingFinalValue(false);
    }
  }, [fetchParams, fetchRawValue, getLegerBody, hasCompleteIdentity]);

  useEffect(() => {
    if (!loading && selectedSemesterId && hasCompleteIdentity) {
      fetchLegerData();
    }
  }, [fetchLegerData, hasCompleteIdentity, loading, selectedSemesterId]);

  useEffect(() => {
    if (!loading && selectedSemesterId && !hasCompleteIdentity) {
      swal({
        title: "Data login belum lengkap",
        text: "Cabang, sekolah, atau ID guru tidak ditemukan. Silakan login ulang.",
        icon: "warning",
      });
    }
  }, [hasCompleteIdentity, loading, selectedSemesterId]);

  const selectedType = useMemo(
    () =>
      typeLegerData.find(
        (item) => String(item?.id) === String(selectedTypeLeger)
      ) || null,
    [selectedTypeLeger, typeLegerData]
  );

  const selectedTypeIndex = useMemo(
    () =>
      typeLegerData.findIndex(
        (item) => String(item?.id) === String(selectedTypeLeger)
      ),
    [selectedTypeLeger, typeLegerData]
  );

  const isRawValueTab = useMemo(() => {
    const name = String(selectedType?.name_type || "").toLowerCase();
    return (
      name.includes("nilai mentah") ||
      name.includes("raw value") ||
      selectedTypeIndex === 1
    );
  }, [selectedType, selectedTypeIndex]);

  const isFinalValueTab = useMemo(() => {
    const name = String(selectedType?.name_type || "").toLowerCase();
    return (
      name.includes("nilai akhir") ||
      name.includes("final value") ||
      (!isRawValueTab && selectedTypeIndex === 0)
    );
  }, [isRawValueTab, selectedType, selectedTypeIndex]);

  const filteredFinalStudents = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return finalValueStudents;
    return finalValueStudents.filter((student) =>
      String(student?.nama_lengkap || "").toLowerCase().includes(keyword)
    );
  }, [finalValueStudents, searchKeyword]);

  const filteredRawStudents = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return rawValueStudents;
    return rawValueStudents.filter((student) =>
      String(student?.nama_lengkap || "").toLowerCase().includes(keyword)
    );
  }, [rawValueStudents, searchKeyword]);

  const getFinalStudentValue = useCallback((student, header) => {
    if (!header?.matpel_id) return student?.nama_lengkap || "-";

    const score = Array.isArray(student?.nilai)
      ? student.nilai.find(
          (item) => String(item?.matpel_id) === String(header?.matpel_id)
        )
      : null;

    return score?.nilai_rapor ?? "-";
  }, []);

  const rawLeafHeaders = useMemo(
    () =>
      rawValueHeaders.flatMap((header) => {
        const children = Array.isArray(header?.ysb_matpel_tp)
          ? header.ysb_matpel_tp
          : [];

        return children.length
          ? children.map((child) => ({
              ...child,
              parentTitle: header?.title || "-",
              isTp: true,
            }))
          : [{ ...header, parentTitle: header?.title || "-", isTp: false }];
      }),
    [rawValueHeaders]
  );

  const getRawStudentValue = useCallback((student, header) => {
    const title = String(header?.title || header?.parentTitle || "").toLowerCase();

    if (title === "nama") return student?.nama_lengkap || "-";

    if (header?.isTp) {
      const score = Array.isArray(student?.data_nilai)
        ? student.data_nilai.find(
            (item) => String(item?.ysb_matpel_id) === String(header?.id)
          )
        : null;
      return score?.score ?? "-";
    }

    const keyMap = {
      "rata-rata sumatif harian": "rata_rata_sumatif_harian",
      asts: "asts",
      asas: "asas",
      "rata-rata sumatif akhir": "rata_rata_sumatif_akhir",
      "nilai rapor": "nilai_rapor",
    };
    const key = keyMap[title];
    return key ? student?.[key] ?? "-" : "-";
  }, []);

  const getRawWeightInfo = useCallback(
    (title) => {
      const normalized = String(title || "").trim().toLowerCase();
      const format = (value) =>
        value === null || value === undefined || value === "" ? "-" : `${value}%`;

      if (normalized === "rata-rata sumatif akhir") {
        return `Bobot ASTS: ${format(rawValueWeights?.asts)}\nBobot ASAS: ${format(
          rawValueWeights?.asas
        )}`;
      }

      if (normalized === "nilai rapor") {
        return `Bobot Sumatif Harian: ${format(
          rawValueWeights?.sumatif_harian
        )}\nBobot Sumatif Akhir: ${format(rawValueWeights?.sumatif_akhir)}`;
      }

      return "";
    },
    [rawValueWeights]
  );

  const downloadExcel = useCallback((rows, fileName) => {
    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

    const html = `<html><head><meta charset="UTF-8"></head><body><table border="1">${rows
      .map(
        (row, rowIndex) =>
          `<tr>${row
            .map(
              (cell) =>
                `<${rowIndex === 0 ? "th" : "td"}>${escapeHtml(cell)}</${
                  rowIndex === 0 ? "th" : "td"
                }>`
            )
            .join("")}</tr>`
      )
      .join("")}</table></body></html>`;
    const blob = new Blob(["\ufeff", html], {
      type: "application/vnd.ms-excel;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const handleDownload = () => {
    if (isFinalValueTab && filteredFinalStudents.length) {
      downloadExcel(
        [
          ["No", ...finalValueHeaders.map((header) => header?.title || "-")],
          ...filteredFinalStudents.map((student, index) => [
            index + 1,
            ...finalValueHeaders.map((header) =>
              getFinalStudentValue(student, header)
            ),
          ]),
        ],
        `Leger-Nilai-Akhir-${selectedAcademicYear}`
      );
    }

    if (isRawValueTab && filteredRawStudents.length) {
      const subject = subjects.find(
        (item) => String(item.id) === String(selectedSubjectId)
      );
      downloadExcel(
        [
          [
            "No",
            ...rawLeafHeaders.map(
              (header) => header?.name_tp || header?.title || "-"
            ),
          ],
          ...filteredRawStudents.map((student, index) => [
            index + 1,
            ...rawLeafHeaders.map((header) => getRawStudentValue(student, header)),
          ]),
        ],
        `Leger-Nilai-Mentah-${subject?.name || "Mapel"}-${selectedAcademicYear}`
      );
    }
  };

  const mainLoading = loading || loadingTypeLeger;
  const tableLoading = isRawValueTab ? loadingRawValue : loadingFinalValue;
  const displayedStudents = isRawValueTab
    ? filteredRawStudents
    : filteredFinalStudents;

  void permission;

  return (
    <>
      <style>{`
        .leger-body-content {
          width: 100%;
          background: #ffffff;
          border-radius: 18px;
          padding: 20px;
        }

        .leger-filter-top {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 20px;
        }

        .leger-academic-year-wrapper {
          width: 230px;
        }

        .leger-filter-label {
          display: block;
          margin-bottom: 8px;
          color: #334155;
          font-size: 13px;
          font-weight: 700;
        }

        .leger-academic-year-select {
          width: 100%;
          height: 40px;
          padding: 0 38px 0 14px;
          color: #475569;
          font-size: 13px;
          background-color: #ffffff;
          border: 1px solid #dbe2ea;
          border-radius: 9px;
          outline: none;
          cursor: pointer;
          transition: 0.2s ease;
        }

        .leger-academic-year-select:hover {
          border-color: #aeb9c8;
        }

        .leger-academic-year-select:focus {
          border-color: #4964ee;
          box-shadow: 0 0 0 3px rgba(73, 100, 238, 0.1);
        }

        .leger-semester-tabs {
          display: flex;
          align-items: center;
          padding: 4px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
        }

        .leger-semester-button {
          min-width: 78px;
          height: 32px;
          padding: 0 12px;
          color: #64748b;
          font-size: 13px;
          font-weight: 700;
          background: transparent;
          border: none;
          border-radius: 7px;
          cursor: pointer;
          transition: 0.2s ease;
        }

        .leger-semester-button:hover {
          color: #4964ee;
        }

        .leger-semester-button.active {
          color: #4964ee;
          background: #ffffff;
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
        }

        .leger-main-tabs {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
          margin-top: 18px;
          padding: 6px;
          background: #ffffff;
          border: 1px solid #dfe3e8;
          border-radius: 15px;
        }

        .leger-main-tab-button {
          width: 100%;
          height: 44px;
          padding: 0 14px;
          color: #4964ee;
          font-size: 13px;
          font-weight: 700;
          background: #ffffff;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: 0.2s ease;
        }

        .leger-main-tab-button:hover {
          background: #f5f7ff;
        }

        .leger-main-tab-button.active {
          color: #ffffff;
          background: #4964ee;
          box-shadow: 0 4px 10px rgba(73, 100, 238, 0.22);
        }

        .leger-action-row {
          display: flex;
          align-items: center;
          gap: 18px;
          margin-top: 26px;
        }

        .leger-search-input {
          flex: 1;
          min-width: 0;
          height: 42px;
          padding: 0 10px;
          color: #334155;
          font-size: 13px;
          background: #ffffff;
          border: 1px solid #dfe3e8;
          border-radius: 14px;
          outline: none;
          transition: 0.2s ease;
        }

        .leger-search-input::placeholder {
          color: #999999;
        }

        .leger-search-input:focus {
          border-color: #4964ee;
          box-shadow: 0 0 0 3px rgba(73, 100, 238, 0.08);
        }

        .leger-download-button {
          min-width: 145px;
          height: 42px;
          padding: 0 18px;
          color: #ffffff;
          font-size: 12px;
          font-weight: 700;
          white-space: nowrap;
          background: #e5a000;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: 0.2s ease;
        }

        .leger-download-button:hover {
          background: #cf9000;
        }

        .leger-result-container {
          width: 100%;
          min-height: 345px;
          margin-top: 16px;
          padding: 16px 18px;
          background: #ffffff;
          border: 1px solid #dfe3e8;
          border-radius: 15px;
        }

        .leger-empty-message {
          color: #64748b;
          font-size: 13px;
          font-weight: 600;
        }

        .leger-loading-container {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 450px;
        }

        .leger-type-tabs-wrapper {
          width: 100%;
          padding: 18px 0 10px;
        }

        .leger-type-tabs {
          display: flex;
          align-items: center;
          width: 100%;
          min-height: 54px;
          padding: 5px;
          overflow-x: auto;
          border: 1px solid #f1f5fb;
          background: #ffffff;
          scrollbar-width: thin;
        }

        .leger-type-tab {
          flex: 1 0 140px;
          min-width: 140px;
          padding: 11px 18px;
          color: #3f5bea;
          font-size: 13px;
          font-weight: 700;
          line-height: 1.3;
          text-align: center;
          white-space: nowrap;
          background: transparent;
          border: 0;
          border-radius: 9px;
          cursor: pointer;
        }

        .leger-type-tab:hover { background: #eef2ff; }

        .leger-type-tab.active {
          color: #ffffff;
          background: #3f5bea;
          box-shadow: 0 4px 10px rgba(63, 91, 234, 0.22);
        }

        .leger-type-tab-state,
        .leger-empty-content {
          width: 100%;
          padding: 25px;
          color: #94a3b8;
          font-size: 14px;
          text-align: center;
        }

        .leger-final-toolbar {
          display: flex;
          align-items: center;
          gap: 16px;
          margin: 12px 0 16px;
        }

        .leger-final-search,
        .leger-raw-subject {
          height: 46px;
          padding: 0 18px;
          color: #1f2937;
          font-size: 14px;
          background: #ffffff;
          border: 1px solid #dbe3f0;
          border-radius: 13px;
          outline: none;
        }

        .leger-final-search { flex: 1; width: 100%; }
        .leger-raw-subject { width: 280px; min-width: 220px; }

        .leger-final-search:focus,
        .leger-raw-subject:focus {
          border-color: #3f5bea;
          box-shadow: 0 0 0 3px rgba(63, 91, 234, 0.1);
        }

        .leger-final-download {
          min-width: 154px;
          height: 46px;
          padding: 0 20px;
          color: #ffffff;
          font-size: 13px;
          font-weight: 700;
          background: #f0a000;
          border: 0;
          border-radius: 13px;
          cursor: pointer;
        }

        .leger-final-download:disabled { cursor: not-allowed; opacity: 0.55; }

        .leger-final-table-wrap {
          width: 100%;
          max-height: calc(100vh - 250px);
          overflow: auto;
          scrollbar-color: #cbd5e1 transparent;
          scrollbar-width: thin;
          -webkit-overflow-scrolling: touch;
        }

        .leger-final-table {
          width: max-content;
          min-width: 100%;
          color: #334155;
          font-size: 13px;
          border-collapse: separate;
          border-spacing: 0;
        }

        .leger-final-table th,
        .leger-final-table td {
          min-width: 165px;
          height: 72px;
          padding: 14px 16px;
          text-align: center;
          white-space: nowrap;
          border-bottom: 1px solid #e2e8f0;
          border-left: 1px solid #e2e8f0;
        }

        .leger-final-table th:last-child,
        .leger-final-table td:last-child { border-right: 1px solid #e2e8f0; }

        .leger-final-table th {
          position: sticky;
          top: 0;
          z-index: 6;
          height: 70px;
          color: #172554;
          font-weight: 700;
          white-space: normal;
          vertical-align: middle;
          background: #f2f3fc;
        }

        .leger-final-table thead tr:nth-child(2) th { top: 70px; z-index: 7; }
        .leger-final-table tbody tr:nth-child(even) td { background: #f8fafc; }
        .leger-final-table tbody tr:nth-child(odd) td { background: #ffffff; }

        .leger-final-header-title {
          display: block;
          max-width: 220px;
          line-height: 1.35;
          white-space: normal;
          overflow-wrap: break-word;
        }

        .leger-final-header-subtitle {
          display: block;
          margin-top: 3px;
          color: #52607a;
          font-size: 11px;
          font-weight: 500;
        }

        .leger-final-table th:nth-child(2),
        .leger-final-table td:nth-child(2) {
          position: sticky;
          left: 68px;
          z-index: 2;
          min-width: 250px;
          max-width: 250px;
          text-align: left;
          white-space: normal;
          box-shadow: 5px 0 10px -10px rgba(0, 76, 255, 0.55);
        }

        .leger-final-table th:nth-child(2) { z-index: 8; background: #f2f3fc; }

        .leger-final-table th:first-child,
        .leger-final-table td:first-child {
          position: sticky;
          left: 0;
          z-index: 3;
          width: 68px;
          min-width: 68px;
          max-width: 68px;
          padding: 8px;
        }

        .leger-final-table th:first-child { z-index: 9; background: #f2f3fc; }

        .leger-final-number {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          color: #3f5bea;
          font-size: 13px;
          font-weight: 700;
          background: #f5f7ff;
          border: 1px solid #d4ddff;
          border-radius: 50%;
        }

        .leger-final-empty {
          padding: 32px 18px !important;
          color: #94a3b8;
          text-align: center !important;
        }

        .leger-raw-header-label {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
        }

        .leger-raw-weight-info {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 19px;
          height: 19px;
          padding: 0;
          color: #ffffff;
          font-size: 12px;
          font-weight: 800;
          background: #3f5bea;
          border: 0;
          border-radius: 50%;
          cursor: help;
        }

        .leger-raw-weight-info::after {
          content: attr(data-tooltip);
          position: absolute;
          z-index: 40;
          top: calc(100% + 10px);
          right: 50%;
          width: max-content;
          max-width: 290px;
          padding: 9px 11px;
          color: #ffffff;
          font-size: 11px;
          font-weight: 500;
          line-height: 1.45;
          text-align: left;
          white-space: pre-line;
          visibility: hidden;
          background: #172554;
          border-radius: 8px;
          opacity: 0;
          transform: translateX(50%) translateY(-4px);
          pointer-events: none;
        }

        .leger-raw-weight-info:hover::after,
        .leger-raw-weight-info:focus-visible::after {
          visibility: visible;
          opacity: 1;
          transform: translateX(50%) translateY(0);
        }

        @media screen and (max-width: 768px) {
          .leger-body-content {
            padding: 16px;
          }

          .leger-filter-top {
            align-items: stretch;
            flex-direction: column;
            gap: 14px;
          }

          .leger-academic-year-wrapper {
            width: 100%;
          }

          .leger-semester-tabs {
            width: 100%;
          }

          .leger-semester-button {
            flex: 1;
          }

          .leger-action-row {
            gap: 12px;
          }

          .leger-final-toolbar {
            align-items: stretch;
            flex-direction: column;
            gap: 10px;
          }

          .leger-raw-subject,
          .leger-final-download { width: 100%; }
        }

        @media screen and (max-width: 576px) {
          .leger-main-tabs {
            grid-template-columns: 1fr;
          }

          .leger-action-row {
            align-items: stretch;
            flex-direction: column;
          }

          .leger-download-button {
            width: 100%;
          }

          .leger-result-container {
            min-height: 300px;
          }
        }
      `}</style>

      <div className="body sims-page">
        <div className="page-header-card">
          <div className="page-header-top d-flex">
            <div className="page-title-wrap">
              <span className="page-title-icon">
                <FontAwesomeIcon
                  icon={faRectangleList}
                />
              </span>

              <h4 className="page-title-text">
                Leger
              </h4>
            </div>
          </div>
        </div>

        <div className="body-content sims-content leger-body-content">
          {mainLoading ? (
            <div className="leger-loading-container">
              <PulseLoader
                color="#4964ee"
                size={10}
              />
            </div>
          ) : (
            <>
              <div className="leger-filter-top">
                <div className="leger-academic-year-wrapper">
                  <label className="leger-filter-label">
                    Tahun Ajaran
                  </label>

                  <select
                    className="leger-academic-year-select"
                    value={selectedAcademicYear}
                    onChange={handleAcademicYearChange}
                  >
                    {academicYears.length === 0 && (
                      <option value="">
                        Tahun ajaran tidak tersedia
                      </option>
                    )}

                    {academicYears.map(
                      (academicYear) => (
                        <option
                          key={academicYear}
                          value={academicYear}
                        >
                          {academicYear}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div className="leger-semester-tabs">
                  {semestersByAcademicYear.length ===
                    0 && (
                    <button
                      type="button"
                      className="leger-semester-button"
                      disabled
                    >
                      Semester tidak tersedia
                    </button>
                  )}

                  {semestersByAcademicYear.map(
                    (semester) => {
                      const isActive =
                        String(selectedSemesterId) ===
                        String(semester?.id);

                      return (
                        <button
                          key={semester?.id}
                          type="button"
                          className={`leger-semester-button ${
                            isActive ? "active" : ""
                          }`}
                          onClick={() =>
                            handleSemesterChange(
                              semester
                            )
                          }
                        >
                          {getSemesterLabel(
                            semester
                          )}
                        </button>
                      );
                    }
                  )}
                </div>
              </div>

              <div className="leger-type-tabs-wrapper">
                <div className="leger-type-tabs">
                  {typeLegerData.length > 0 ? (
                    typeLegerData.map((typeLeger, index) => (
                      <button
                        type="button"
                        key={typeLeger?.id || index}
                        className={`leger-type-tab ${
                          String(selectedTypeLeger) === String(typeLeger?.id)
                            ? "active"
                            : ""
                        }`}
                        onClick={() => {
                          setSelectedTypeLeger(typeLeger?.id || "");
                          setSearchKeyword("");
                        }}
                      >
                        {typeLeger?.name_type || "-"}
                      </button>
                    ))
                  ) : (
                    <div className="leger-type-tab-state">
                      Tipe leger belum tersedia
                    </div>
                  )}
                </div>
              </div>

              {(isFinalValueTab || isRawValueTab) && (
                <div className="leger-final-toolbar">
                  {isRawValueTab && (
                    <select
                      className="leger-raw-subject"
                      value={selectedSubjectId}
                      onChange={(event) => {
                        const subjectId = event.target.value;
                        setSelectedSubjectId(subjectId);
                        setSearchKeyword("");
                        fetchRawValue(subjectId);
                      }}
                      disabled={subjects.length === 0 || loadingRawValue}
                    >
                      {subjects.length === 0 && (
                        <option value="">Mata pelajaran tidak tersedia</option>
                      )}
                      {subjects.map((subject) => (
                        <option key={subject.id} value={subject.id}>
                          {subject.name}
                        </option>
                      ))}
                    </select>
                  )}

                  <input
                    type="search"
                    className="leger-final-search"
                    placeholder="Cari Nama Siswa"
                    value={searchKeyword}
                    onChange={(event) => setSearchKeyword(event.target.value)}
                  />

                  <button
                    type="button"
                    className="leger-final-download"
                    onClick={handleDownload}
                    disabled={tableLoading || displayedStudents.length === 0}
                  >
                    {isRawValueTab ? "Unduh Nilai Mentah" : "Unduh Nilai Akhir"}
                  </button>
                </div>
              )}

              {tableLoading ? (
                <div className="leger-loading-container">
                  <PulseLoader color="#3f5bea" size={10} />
                </div>
              ) : isFinalValueTab ? (
                <div className="leger-final-table-wrap">
                  <table className="leger-final-table">
                    <thead>
                      <tr>
                        <th className="text-center">No</th>
                        {finalValueHeaders.map((header, index) => (
                          <th
                            key={header?.matpel_id || `${header?.title}-${index}`}
                          >
                            <span className="leger-final-header-title">
                              {header?.title || "-"}
                            </span>
                            {header?.matpel_id && (
                              <span className="leger-final-header-subtitle">
                                Nilai Rapor
                              </span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFinalStudents.length > 0 ? (
                        filteredFinalStudents.map((student, studentIndex) => (
                          <tr key={student?.ysb_student_id || studentIndex}>
                            <td className="text-center">
                              <span className="leger-final-number">
                                {studentIndex + 1}
                              </span>
                            </td>
                            {finalValueHeaders.map((header, headerIndex) => (
                              <td
                                key={
                                  header?.matpel_id ||
                                  `${header?.title}-${headerIndex}`
                                }
                              >
                                {getFinalStudentValue(student, header)}
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            className="leger-final-empty"
                            colSpan={Math.max(finalValueHeaders.length + 1, 1)}
                          >
                            {searchKeyword
                              ? "Nama siswa tidak ditemukan"
                              : "Nilai akhir semester belum tersedia"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : isRawValueTab ? (
                <div className="leger-final-table-wrap">
                  <table className="leger-final-table">
                    <thead>
                      <tr>
                        <th rowSpan={2} className="text-center">No</th>
                        {rawValueHeaders.map((header, index) => {
                          const children = Array.isArray(header?.ysb_matpel_tp)
                            ? header.ysb_matpel_tp
                            : [];
                          const weightInfo = getRawWeightInfo(header?.title);

                          return (
                            <th
                              key={`${header?.title || "header"}-${index}`}
                              colSpan={Math.max(
                                children.length,
                                Number(header?.colspan) || 1
                              )}
                              rowSpan={children.length ? 1 : 2}
                              className="text-center"
                            >
                              <span className="leger-raw-header-label">
                                <span className="leger-final-header-title">
                                  {header?.title || "-"}
                                </span>
                                {weightInfo && (
                                  <button
                                    type="button"
                                    className="leger-raw-weight-info"
                                    data-tooltip={weightInfo}
                                    aria-label={`Lihat bobot ${header?.title || "nilai"}`}
                                  >
                                    !
                                  </button>
                                )}
                              </span>
                            </th>
                          );
                        })}
                      </tr>
                      <tr>
                        {rawValueHeaders.flatMap((header, headerIndex) =>
                          (Array.isArray(header?.ysb_matpel_tp)
                            ? header.ysb_matpel_tp
                            : []
                          ).map((tp, tpIndex) => (
                            <th key={tp?.id || `${headerIndex}-${tpIndex}`} className="text-center">
                              <span className="leger-final-header-title">
                                {tp?.name_tp || "-"}
                              </span>
                            </th>
                          ))
                        )}
                      </tr>
                    </thead>

                    <tbody>
                      {filteredRawStudents.length > 0 ? (
                        filteredRawStudents.map((student, studentIndex) => (
                          <tr key={student?.ysb_student_id || studentIndex}>
                            <td className="text-center">
                              <span className="leger-final-number">
                                {studentIndex + 1}
                              </span>
                            </td>
                            {rawLeafHeaders.map((header, headerIndex) => (
                              <td
                                key={
                                  header?.id ||
                                  `${header?.parentTitle}-${headerIndex}`
                                }
                              >
                                {getRawStudentValue(student, header)}
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            className="leger-final-empty"
                            colSpan={Math.max(rawLeafHeaders.length + 1, 1)}
                          >
                            {searchKeyword
                              ? "Nama siswa tidak ditemukan"
                              : selectedSubjectId
                              ? "Nilai mentah belum tersedia"
                              : "Mata pelajaran belum tersedia"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="leger-empty-content">Leger belum tersedia</div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
